import { useMemo, useCallback } from 'react';

import { useLocation } from 'react-router-dom';

import GavelIcon from '@mui/icons-material/Gavel';
import GolfCourseIcon from '@mui/icons-material/GolfCourse';
import PolicyIcon from '@mui/icons-material/Policy';

import { useData } from '../contextProviders/DataProvider';
import { usePersonMutators } from '../dataMutators/usePersonMutators';
import { ActionNotice } from '../types/ActionNotice';
import { GOLFER_PRODUCT_ACCESS_STATUSES } from '../types/GolferProductAccess';

import usePersonDetails from './usePersonDetails';

export default function useActionNotices(): { actionNotices: ActionNotice[] } {
  const { pathname } = useLocation();
  const { userState } = useData();
  const { updatePersonDetails } = usePersonMutators();

  const userProfile = userState.status === 'ready' ? userState.userProfile : undefined;
  const { personDetails } = usePersonDetails(userProfile ?? undefined);

  const handleAcceptTos = useCallback(() => {
    if (userProfile?.itemId) {
      updatePersonDetails({
        itemId: userProfile.itemId,
        tosAccepted: true,
      });
    }
  }, [userProfile?.itemId, updatePersonDetails]);

  const handleAcceptPrivacy = useCallback(() => {
    if (userProfile?.itemId) {
      updatePersonDetails({
        itemId: userProfile.itemId,
        privacyPolicyAccepted: true,
      });
    }
  }, [userProfile?.itemId, updatePersonDetails]);

  // The one-time explanation that Gratigolf posts scores to the USGA, dismissed with "Got it".
  const handleUsgaPostingExplained = useCallback(() => {
    if (userProfile?.itemId) {
      updatePersonDetails({
        itemId: userProfile.itemId,
        uiPreferences: { ...personDetails?.uiPreferences, usgaPostingExplained: 'true' },
      });
    }
  }, [userProfile?.itemId, personDetails?.uiPreferences, updatePersonDetails]);

  const actionNotices = useMemo(() => {
    // Suppress all action notices on legal pages that have their own acceptance flow
    if (pathname === '/terms' || pathname === '/privacy') return [];

    if (!personDetails) return [];

    const notices: ActionNotice[] = [];

    if (!personDetails.tosAccepted) {
      notices.push({
        id: 'tos-acceptance',
        title: 'Our Terms of Service',
        text: "Please review and accept Gratigolf's usage policy.",
        severity: 'warning',
        icon: GavelIcon,
        link: '/terms',
        actionLabel: 'Accept',
        onAction: handleAcceptTos,
      });
    }

    if (!personDetails.privacyPolicyAccepted) {
      notices.push({
        id: 'privacy-acceptance',
        title: 'Privacy Policy',
        text: 'Please review and accept how we protect and use your Gratigolf information.',
        severity: 'warning',
        icon: PolicyIcon,
        link: '/privacy',
        actionLabel: 'Accept',
        onAction: handleAcceptPrivacy,
      });
    }

    // USGA score posting. golferProductAccess is copied into the person's details by the server, only
    // once Gratigolf has asked the USGA about this golfer. Both notices open the profile, whose
    // Details show the status and what to do.
    const usgaAccess = personDetails.golferProductAccess;
    const isApproved = usgaAccess?.status === GOLFER_PRODUCT_ACCESS_STATUSES.APPROVED;
    const waitingCount = usgaAccess?.waitingScoreCount ?? 0;
    if (usgaAccess && !isApproved && waitingCount > 0) {
      notices.push({
        id: 'usga-access-waiting',
        title: waitingCount === 1 ? 'A score is waiting for your USGA approval' : `${waitingCount} scores are waiting for your USGA approval`,
        text: 'Check for an email from the USGA (sent to the address you gave them) and acknowledge Gratigolf\'s access to your golf scoring record, so your scores can be posted.',
        severity: 'warning',
        icon: GolfCourseIcon,
        link: '/profile',
      });
    } else if (usgaAccess && !isApproved && personDetails.uiPreferences?.usgaPostingExplained !== 'true') {
      notices.push({
        id: 'usga-posting-explained',
        title: 'Gratigolf posts your scores to the USGA',
        text: 'You\'ll receive an email from the USGA, at the address you gave them, asking you to acknowledge Gratigolf\'s access to your golf scoring record. Tap "Scoring Record Request", then "Acknowledge".',
        severity: 'info',
        icon: GolfCourseIcon,
        link: '/profile',
        actionLabel: 'Got it',
        onAction: handleUsgaPostingExplained,
      });
    }

    return notices;
  }, [personDetails, pathname, handleAcceptTos, handleAcceptPrivacy, handleUsgaPostingExplained]);

  return { actionNotices };
}
